import { useCallback, useEffect, useMemo, useState } from "react";

import { useGetCurrencies } from "@api";
import { useGetPaygapSettings, useUpdatePaygapSettings } from "@api/hooks/paygap";
import { useUserStore } from "@store/user";

export interface EducationTrainingItem {
  id: string;
  numberOfYears: string;
  name: string;
}

export interface ProfessionalPositionItem {
  id: string;
  name: string;
}

export interface GradingItem {
  id: string;
  name: string;
}

export interface AnalysisParameters {
  referenceYear: string;
  standardWorkweek: string;
  ageFormat: string;
  serviceFormat: string;
  currency: string;
  regression: string;
  educationAndTraining: EducationTrainingItem[];
  professionalPositions: ProfessionalPositionItem[];
  grading1: GradingItem[];
  grading2: GradingItem[];
  grading3: GradingItem[];
}

const sanitizeParameters = (
  data: Partial<AnalysisParameters> | null | undefined,
): AnalysisParameters => {
  const education = data?.educationAndTraining?.slice(0, 8).filter(Boolean) || [];
  const professional =
    data?.professionalPositions?.slice(0, 5).filter(Boolean) || [];
  const grading1 = data?.grading1?.slice(0, 200).filter(Boolean) || [];
  const grading2 = data?.grading2?.slice(0, 200).filter(Boolean) || [];
  const grading3 = data?.grading3?.slice(0, 200).filter(Boolean) || [];

  return {
    referenceYear: data?.referenceYear ?? "",
    standardWorkweek: data?.standardWorkweek ?? "40",
    ageFormat: data?.ageFormat ?? "age_years",
    serviceFormat: data?.serviceFormat ?? "years",
    currency: data?.currency ?? "",
    regression: data?.regression ?? "1",
    educationAndTraining: education.map((item) => ({
      id: item.id ?? "",
      numberOfYears: item.numberOfYears ?? "",
      name: item.name ?? "",
    })),
    professionalPositions: professional.map((item) => ({
      id: item.id ?? "",
      name: item.name ?? "",
    })),
    grading1: grading1.map((item) => ({
      id: item.id ?? "",
      name: item.name ?? "",
    })),
    grading2: grading2.map((item) => ({
      id: item.id ?? "",
      name: item.name ?? "",
    })),
    grading3: grading3.map((item) => ({
      id: item.id ?? "",
      name: item.name ?? "",
    })),
  };
};

export const usePaygapParameters = (): {
  parameters: AnalysisParameters;
  handleParameterChange: (
    field: keyof AnalysisParameters,
    value: string | number,
  ) => void;
  handleEducationAndTrainingChange: (
    index: number,
    field: keyof EducationTrainingItem,
    value: string | number,
  ) => void;
  handleProfessionalPositionChange: (
    index: number,
    field: keyof ProfessionalPositionItem,
    value: string | number,
  ) => void;
  addEducationAndTrainingRow: () => void;
  removeEducationAndTrainingRow: (index: number) => void;
  addProfessionalPositionRow: () => void;
  removeProfessionalPositionRow: (index: number) => void;
  handleGrading1Change: (index: number, field: keyof GradingItem, value: string) => void;
  handleGrading2Change: (index: number, field: keyof GradingItem, value: string) => void;
  handleGrading3Change: (index: number, field: keyof GradingItem, value: string) => void;
  addGrading1Row: () => void;
  addGrading2Row: () => void;
  addGrading3Row: () => void;
  removeGrading1Row: (index: number) => void;
  removeGrading2Row: (index: number) => void;
  removeGrading3Row: (index: number) => void;
  saveParameters: () => void;
  hasIdConflicts: boolean;
  currencies: Array<{ code: string; name: string; symbol: string }>;
  currenciesEnabled: boolean;
  setParameters: (updater: (prev: AnalysisParameters) => AnalysisParameters) => void;
  isSaving: boolean;
  isLoading: boolean;
  showSaveSuccess: boolean;
} => {
  const { user } = useUserStore();
  const { currencies: apiCurrencies, currenciesEnabled } = useGetCurrencies(user);

  // Fetch settings from API
  const { settings, isLoadingPaygapSettings } = useGetPaygapSettings(user, true);
  const { updateSettings, isPendingUpdateSettings } = useUpdatePaygapSettings(user);

  const [parameters, setParameters] = useState<AnalysisParameters>(() =>
    sanitizeParameters(null),
  );
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  // Initialize from API when settings load
  useEffect(() => {
    if (settings?.analysisParameters) {
      setParameters(
        sanitizeParameters(
          settings.analysisParameters as Partial<AnalysisParameters>,
        ),
      );
    }
  }, [settings?.analysisParameters]);

  const handleParameterChange = useCallback(
    (field: keyof AnalysisParameters, value: string | number): void => {
      setParameters((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  const handleEducationAndTrainingChange = useCallback(
    (
      index: number,
      field: keyof EducationTrainingItem,
      value: string | number,
    ): void => {
      setParameters((prev) => {
        const updated = [...prev.educationAndTraining];

        updated[index] = {
          ...updated[index],
          [field]: String(value),
        };

return {
          ...prev,
          educationAndTraining: updated,
        };
      });
    },
    [],
  );

  const handleProfessionalPositionChange = useCallback(
    (
      index: number,
      field: keyof ProfessionalPositionItem,
      value: string | number,
    ): void => {
      setParameters((prev) => {
        const updated = [...prev.professionalPositions];

        updated[index] = {
          ...updated[index],
          [field]: String(value),
        };

return {
          ...prev,
          professionalPositions: updated,
        };
      });
    },
    [],
  );

  const addEducationAndTrainingRow = useCallback((): void => {
    setParameters((prev) => {
      if (prev.educationAndTraining.length >= 8) {
        return prev;
      }

return {
        ...prev,
        educationAndTraining: [
          ...prev.educationAndTraining,
          { id: "", numberOfYears: "", name: "" },
        ],
      };
    });
  }, []);

  const removeEducationAndTrainingRow = useCallback((index: number): void => {
    setParameters((prev) => {
      const updated = prev.educationAndTraining.filter((_, i) => i !== index);


return { ...prev, educationAndTraining: updated };
    });
  }, []);

  const addProfessionalPositionRow = useCallback((): void => {
    setParameters((prev) => {
      if (prev.professionalPositions.length >= 5) {
        return prev;
      }

return {
        ...prev,
        professionalPositions: [...prev.professionalPositions, { id: "", name: "" }],
      };
    });
  }, []);

  const removeProfessionalPositionRow = useCallback((index: number): void => {
    setParameters((prev) => {
      const updated = prev.professionalPositions.filter((_, i) => i !== index);


return { ...prev, professionalPositions: updated };
    });
  }, []);

  const handleGrading1Change = useCallback(
    (index: number, field: keyof GradingItem, value: string): void => {
      setParameters((prev) => {
        const updated = [...prev.grading1];

        updated[index] = { ...updated[index], [field]: value };

return { ...prev, grading1: updated };
      });
    },
    [],
  );

  const handleGrading2Change = useCallback(
    (index: number, field: keyof GradingItem, value: string): void => {
      setParameters((prev) => {
        const updated = [...prev.grading2];

        updated[index] = { ...updated[index], [field]: value };

return { ...prev, grading2: updated };
      });
    },
    [],
  );

  const handleGrading3Change = useCallback(
    (index: number, field: keyof GradingItem, value: string): void => {
      setParameters((prev) => {
        const updated = [...prev.grading3];

        updated[index] = { ...updated[index], [field]: value };

return { ...prev, grading3: updated };
      });
    },
    [],
  );

  const addGrading1Row = useCallback((): void => {
    setParameters((prev) => {
      if (prev.grading1.length >= 200) return prev;

return { ...prev, grading1: [...prev.grading1, { id: "", name: "" }] };
    });
  }, []);

  const addGrading2Row = useCallback((): void => {
    setParameters((prev) => {
      if (prev.grading2.length >= 200) return prev;

return { ...prev, grading2: [...prev.grading2, { id: "", name: "" }] };
    });
  }, []);

  const addGrading3Row = useCallback((): void => {
    setParameters((prev) => {
      if (prev.grading3.length >= 200) return prev;

return { ...prev, grading3: [...prev.grading3, { id: "", name: "" }] };
    });
  }, []);

  const removeGrading1Row = useCallback((index: number): void => {
    setParameters((prev) => ({
      ...prev,
      grading1: prev.grading1.filter((_, i) => i !== index),
    }));
  }, []);

  const removeGrading2Row = useCallback((index: number): void => {
    setParameters((prev) => ({
      ...prev,
      grading2: prev.grading2.filter((_, i) => i !== index),
    }));
  }, []);

  const removeGrading3Row = useCallback((index: number): void => {
    setParameters((prev) => ({
      ...prev,
      grading3: prev.grading3.filter((_, i) => i !== index),
    }));
  }, []);

  const saveParameters = useCallback((): void => {
    // Save to database via API
    updateSettings({
      analysisParameters: {
        referenceYear: parameters.referenceYear,
        standardWorkweek: parameters.standardWorkweek,
        ageFormat: parameters.ageFormat,
        serviceFormat: parameters.serviceFormat,
        currency: parameters.currency,
        regression: parameters.regression,
        educationAndTraining: parameters.educationAndTraining,
        professionalPositions: parameters.professionalPositions,
        grading1: parameters.grading1,
        grading2: parameters.grading2,
        grading3: parameters.grading3,
      },
    });
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  }, [parameters, updateSettings]);

  const hasIdConflicts = useMemo(() => {
    const educationIds = parameters.educationAndTraining
      .map((i) => i.id.trim())
      .filter(Boolean);
    const professionalIds = parameters.professionalPositions
      .map((i) => i.id.trim())
      .filter(Boolean);
    const grading1Ids = parameters.grading1.map((i) => i.id.trim()).filter(Boolean);
    const grading2Ids = parameters.grading2.map((i) => i.id.trim()).filter(Boolean);
    const grading3Ids = parameters.grading3.map((i) => i.id.trim()).filter(Boolean);

    const hasDupes = (list: string[]): boolean => {
      const set = new Set<string>();

      for (const id of list) {
        if (set.has(id)) return true;
        set.add(id);
      }

return false;
    };

    return (
      hasDupes(educationIds) ||
      hasDupes(professionalIds) ||
      hasDupes(grading1Ids) ||
      hasDupes(grading2Ids) ||
      hasDupes(grading3Ids)
    );
  }, [
    parameters.educationAndTraining,
    parameters.professionalPositions,
    parameters.grading1,
    parameters.grading2,
    parameters.grading3,
  ]);

  type ApiCurrency = {
    code?: string;
    isoCode?: string;
    name?: string;
    label?: string;
    symbol?: string;
    sign?: string;
  };

  const currencies = (
    (apiCurrencies as unknown as ApiCurrency[] | undefined) || []
  ).map((c) => ({
    code: String((c && (c.code ?? c.isoCode)) || ""),
    name: String((c && (c.name ?? c.label)) || ""),
    symbol: String((c && (c.symbol ?? c.sign)) || ""),
  }));

  return {
    parameters,
    currencies,
    currenciesEnabled,
    handleParameterChange,
    handleEducationAndTrainingChange,
    handleProfessionalPositionChange,
    addEducationAndTrainingRow,
    removeEducationAndTrainingRow,
    addProfessionalPositionRow,
    removeProfessionalPositionRow,
    handleGrading1Change,
    handleGrading2Change,
    handleGrading3Change,
    addGrading1Row,
    addGrading2Row,
    addGrading3Row,
    removeGrading1Row,
    removeGrading2Row,
    removeGrading3Row,
    saveParameters,
    hasIdConflicts,
    setParameters,
    isSaving: isPendingUpdateSettings,
    isLoading: isLoadingPaygapSettings,
    showSaveSuccess,
  };
};
